import round from 'lodash/round';

import { calculateDate } from 'bundles/apex/dates/helpers';
import { calculateLeaseTermsFromEscalation } from 'components/lease_structure/form/helpers/lease_term';
import { findEmployerRole } from 'bundles/backoffice/contacts/contact_role_employment';
import { findLocalContact } from 'bundles/apex/deals/helpers/contacts';
import { getUpdatedFieldValue, LEASE_TERM_READ_ONLY_FIELDS } from 'bundles/apex/deals/helpers/fields';
import { loadFields } from 'bundles/apex/deals/form/store/fields/actions';
import { post, put } from 'helpers/api';
import { serializeDeal } from './serializer';
import { updateTransactionSideGrossCommissionValues } from 'bundles/backoffice/vouchers/helpers/vouchers';

import {
  BUY_SIDE,
  SELL_SIDE,
  UNIT_DOLLARS,
  newTransactionSideDetails,
} from 'bundles/backoffice/vouchers/helpers/transaction_side_details';

import {
  buildLeaseStructure,
  calculatesGrossCommissionAmount,
  hasLeaseTerms,
} from 'bundles/apex/deals/lease_terms/helpers';

import {
  getLeaseStructureAggregates,
  updateLeaseStructure
} from 'components/lease_structure/form/helpers/lease_structure';

import { addPrimaryPlanToCommissionChanges, recalculateBrokerCommissions } from './broker_commission_helpers';
import {
  getGrossCommissionAmount,
  getGrossCommissionPercent,
  recalculateCommissionRate,
  recalculateGrossCommissionValues,
  recalculateLeaseRate,
  recalculateTransactionValue,
} from './transaction_helpers';

export function setDealStage(stage) {
  return (dispatch) => {
    dispatch(changeDeal({ stage }));
    dispatch(loadFields());
  };
}

export function setDealType(dealType) {
  return (dispatch) => {
    dispatch(changeDeal({ dealType }));
    dispatch(loadFields());
  };
}

export function setAgreementType(agreementType) {
  return (dispatch) => {
    dispatch(changeDeal({ agreementType }));
  };
}

export function changePropertyType(changes) {
  return (dispatch) => {
    dispatch(changeDeal(changes));
    dispatch(loadFields());
  };
}

export function setCountryId(countryId) {
  return (dispatch) => {
    dispatch(changeDeal({ countryId }));
    dispatch(loadFields());
  };
}

export const CHANGE_DEAL = 'CHANGE_DEAL';
export function changeDeal(changes) {
  return {
    type: CHANGE_DEAL,
    changes,
  };
}

export function setLeaseRate(leaseRate) {
  return changeTransactionValueDependencies({ leaseRate });
}

export function setLeaseRateUnits(leaseRateUnits) {
  return changeLeaseStructureDependencies({ leaseRateUnits });
}

export function setLeaseTerm(leaseTerm) {
  return changeTransactionValueDependencies({ leaseTerm });
}

export function setLeaseTermUnits(leaseTermUnits) {
  return changeTransactionValueDependencies({ leaseTermUnits });
}

export function setSpaceSize(spaceSize) {
  return changeLeaseStructureDependencies({ spaceSize });
}

export function setSpaceSizeUnits(spaceSizeUnits) {
  return changeTransactionValueDependencies({ spaceSizeUnits });
}

function changeTransactionValueDependencies(changes) {
  return (dispatch, getState) => {
    const { deal } = getState();

    changes.transactionValue = recalculateTransactionValue({ ...deal, ...changes });
    changes = { ...changes, ...recalculateGrossCommissionValues({ ...deal, ...changes }) };
    changes.commissions = recalculateBrokerCommissions({ ...deal, ...changes });

    dispatch({ type: CHANGE_DEAL, changes });
  };
}

function changeLeaseStructureDependencies(changes) {
  return (dispatch, getState) => {
    const { deal } = getState();

    if (hasLeaseTerms(deal)) {
      dispatch({ type: CHANGE_DEAL, changes });
      dispatch(setLeaseTerms(deal.leaseTerms));
    } else {
      dispatch(changeTransactionValueDependencies(changes));
    }
  };
}

export function setTransactionValue(transactionValue) {
  return (dispatch, getState) => {
    const { deal } = getState();

    let changes = { transactionValue };
    changes.leaseRate = recalculateLeaseRate({ ...deal, ...changes });
    changes = { ...changes, ...recalculateGrossCommissionValues({ ...deal, ...changes }) };
    changes.commissions = recalculateBrokerCommissions({ ...deal, ...changes });

    dispatch({ type: CHANGE_DEAL, changes });
  };
}

export function setGrossCommissionAmount(grossCommissionAmount) {
  return (dispatch, getState) => {
    const { deal } = getState();

    const changes = { grossCommissionAmount };

    if (grossCommissionAmount != null) {
      changes.grossCommissionUnit = 'gross_commission_unit_dollars';

      if (deal.transactionValue != null) {
        changes.grossCommissionPercent = getGrossCommissionPercent(grossCommissionAmount, deal.transactionValue);
      }
    }

    changes.commissionRate = recalculateCommissionRate({ ...deal, ...changes });
    changes.commissions = recalculateBrokerCommissions({ ...deal, ...changes });

    dispatch({ type: CHANGE_DEAL, changes });
  };
}

export function setGrossCommissionPercent(grossCommissionPercent) {
  return (dispatch, getState) => {
    const { deal } = getState();

    const changes = { grossCommissionPercent };

    if (grossCommissionPercent != null) {
      changes.grossCommissionUnit = 'gross_commission_unit_percent';

      if (deal.transactionValue != null) {
        changes.grossCommissionAmount = getGrossCommissionAmount(grossCommissionPercent, deal.transactionValue);
      }
    }

    changes.commissions = recalculateBrokerCommissions({ ...deal, ...changes });

    dispatch({ type: CHANGE_DEAL, changes });
  };
}

export function setCommissionMode(commissionMode) {
  return changeGrossCommissionDependencies({ commissionMode });
}

export function setCommissionRate(commissionRate) {
  return changeGrossCommissionDependencies({ commissionRate });
}

function changeGrossCommissionDependencies(changes) {
  return (dispatch, getState) => {
    const { deal } = getState();

    changes = { ...changes, ...recalculateGrossCommissionValues({ ...deal, ...changes }) };
    changes.commissions = recalculateBrokerCommissions({ ...deal, ...changes });

    dispatch({ type: CHANGE_DEAL, changes });
  };
}

export const ADD_DEAL_USER = 'ADD_DEAL_USER';
export function addDealUser() {
  return {
    type: ADD_DEAL_USER,
  };
}

export const CHANGE_DEAL_USER = 'CHANGE_DEAL_USER';
export function changeDealUser(dealUser, changes) {
  return {
    type: CHANGE_DEAL_USER,
    dealUser,
    changes,
  };
}

export const ADD_CONTACT_ROLE = 'ADD_CONTACT_ROLE';
export function addContactRole(role) {
  return {
    type: ADD_CONTACT_ROLE,
    role,
  };
}

// TODO: The deal form should be hooked up to confirm contact attribute sharing
export const CHANGE_CONTACT_ROLE = 'CHANGE_CONTACT_ROLE';
export function changeContactRole(contactRole, changes) {
  return (dispatch, getState) => {
    const employer = changes.contact?.business;

    if (employer) {
      changes.employer = employer;

      let employerRole = findEmployerRole(employer, contactRole.role, getState().deal.contactRoles);
      if (!employerRole) {
        dispatch(addContactRole(contactRole.role));
        const contactRoles = getState().deal.contactRoles;
        employerRole = contactRoles[contactRoles.length - 1];
      }

      dispatch(changeContactRole(employerRole, { contact: employer }));
    }

    dispatch({
      type: CHANGE_CONTACT_ROLE,
      contactRole,
      changes,
    });
  };
}

export function removeContactRole(contactRole) {
  return changeContactRole(contactRole, { _destroy: true });
}

export function setContactRoleContact(contactRole, contact) {
  return (dispatch, getState) => {
    contact = findLocalContact(contact, getState().deal);
    dispatch(changeContactRole(contactRole, { contact }));
  };
}

export function setRentEscalation(escalation) {
  return (dispatch, getState) => {
    const { deal } = getState();

    dispatch({
      type: CHANGE_DEAL,
      changes: {
        leaseRateUnits: escalation.leaseRateUnits,
        includeOperatingExpenses: escalation.includeOperatingExpenses,
      }
    });

    const leaseStructure = buildLeaseStructure(deal);
    const leaseTerms = calculateLeaseTermsFromEscalation(escalation, leaseStructure);
    dispatch(setLeaseTerms(leaseTerms));
  };
}

export function setIsRentCalculated(isRentCalculated) {
  return (dispatch, getState) => {
    const { deal } = getState();

    dispatch({
      type: CHANGE_DEAL,
      changes: {
        isRentCalculated,
      }
    });

    if (isRentCalculated) {
      dispatch(setLeaseTerms(deal.leaseTerms));
    }
  };
}

export function setLeaseTerms(leaseTerms) {
  return (dispatch, getState) => {
    let { deal } = getState();
    const { fields } = getState();

    const updatedLeaseStructure = updateLeaseStructure(buildLeaseStructure({ ...deal, leaseTerms }));
    ({ leaseTerms } = updatedLeaseStructure);

    dispatch({
      type: CHANGE_DEAL,
      changes: {
        leaseTerms,
      }
    });

    const readOnlyFieldChanges = {};
    const updatedDeal = { ...deal, leaseTerms };
    LEASE_TERM_READ_ONLY_FIELDS.forEach((field) => {
      if (fields[field]) {
        readOnlyFieldChanges[field] = getUpdatedFieldValue(field, updatedDeal);
      }
    });

    if (Object.keys(readOnlyFieldChanges).length) {
      dispatch(changeDeal(readOnlyFieldChanges));
    }

    if (!hasLeaseTerms({ leaseTerms })) {
      // When deleting the last lease term, resetting all values to zero seems less than ideal
      // So, keep whatever the last term was in the summary
      return;
    }

    const aggregates = getLeaseStructureAggregates(updatedLeaseStructure);

    // TODO: aggregates.startDate maps to which deal date?
    const changes = {
      leaseTerm: aggregates.months,
      leaseTermUnits: 'months', // TODO: Set to years when aggregates.months is divisible by 12?
      transactionValue: aggregates.leaseAmount,
    };

    ({ deal } = getState());
    if (calculatesGrossCommissionAmount(deal)) {
      changes.grossCommissionAmount = aggregates.commissionAmount;
      changes.grossCommissionPercent = round(aggregates.commissionPercent, 2);
      changes.grossCommissionUnit = 'gross_commission_unit_dollars';

      if (deal.hasTransactionSides) {
        changes.transactionSideDetails = deal.transactionSideDetails.map(details => ({
          ...details,
          grossAmount: details.transactionSide === BUY_SIDE
            ? aggregates.buySideCommissionAmount
            : aggregates.sellSideCommissionAmount,
          grossPercent: details.transactionSide === BUY_SIDE
            ? aggregates.buySideCommissionPercent
            : aggregates.sellSideCommissionPercent,
          grossUnit: UNIT_DOLLARS,
        }));
      }
    }

    if (deal.hasTransactionSides) {
      if (!changes.transactionSideDetails) changes.transactionSideDetails = deal.transactionSideDetails;
      changes.transactionSideDetails.forEach((details) => {
        details.grossRate = recalculateCommissionRate({ ...deal, ...changes }, details.transactionSide);
      });
    }
    changes.commissionRate = recalculateCommissionRate({ ...deal, ...changes });
    changes.commissions =  recalculateBrokerCommissions({ ...deal, ...changes });
    changes.leaseRate = recalculateLeaseRate({ ...deal, ...changes });

    dispatch({ type: CHANGE_DEAL, changes });
  };
}

export function setDate(name, date) {
  return (dispatch, getState) => {
    const changes = { [name]: date };

    const state = getState();
    const deal = { ...state.deal, ...changes };

    for (const dateName in state.fields) {
      if (!state.fields[dateName].isDate || dateName === name) {
        continue;
      }

      changes[dateName] = {
        ...deal[dateName],
        date: calculateDate(deal, state.fields, dateName, deal[dateName]),
      };
    }

    dispatch(changeDeal(changes));
  };
}

export const ADD_COMMISSION = 'ADD_COMMISSION';
export function addCommission(attributes) {
  return {
    type: ADD_COMMISSION,
    attributes: {
      grossUnit: 'gross_unit_percent',
      grossAmount: 0,
      grossPercent: 0,
      ...attributes,
    },
  };
}

export function removeCommission(commission) {
  return (dispatch, getState) => {
    const { deal, options } = getState();
    const internalCommissions = deal.commissions.filter(commission => !commission.outside && !commission._destroy);

    dispatch(changeCommission(commission, { _destroy: true }));

    if (internalCommissions[0] === commission) {
      const newFirst = internalCommissions[1];
      const userOption = options.users.find(option => option.value === newFirst.broker?.id);
      if (!userOption?.broker) {
        dispatch(changeCommission(newFirst, { broker: null }));
      }
    }
  };
}

export const CHANGE_COMMISSION = 'CHANGE_COMMISSION';
export const CHANGE_COMMISSION_TRANSACTION_SIDE_DETAILS = 'CHANGE_COMMISSION_TRANSACTION_SIDE_DETAILS';
export function changeCommission(commission, changes, transactionSide) {
  return {
    type: transactionSide ? CHANGE_COMMISSION_TRANSACTION_SIDE_DETAILS : CHANGE_COMMISSION,
    commission,
    changes,
    transactionSide
  };
}

export const CHANGE_COMMISSION_TRANSACTION_SIDE_PAYEE = 'CHANGE_COMMISSION_TRANSACTION_SIDE_PAYEE';
export function changeCommissionPayee(commission, changes, transactionSide) {
  return (dispatch, getState) => {
    const { deal, options } = getState();

    changes.contact = findLocalContact(changes.contact, deal);
    addPrimaryPlanToCommissionChanges(changes, options.users);

    dispatch({
      type: transactionSide ? CHANGE_COMMISSION_TRANSACTION_SIDE_PAYEE : CHANGE_COMMISSION,
      commission,
      changes,
      transactionSide
    });
  };
}

export const SET_DEAL = 'SET_DEAL';
export function setDeal(deal) {
  return {
    type: SET_DEAL,
    deal,
  };
}

export function saveDeal(onSuccess) {
  return async (dispatch, getState) => {
    const { deal, fields } = getState();

    dispatch(changeDeal({ errors: null, saving: true }));

    try {
      const makeRequest = deal.id ? put : post;
      const savedDeal = await makeRequest(deal.path, serializeDeal(deal, fields));
      dispatch(setDeal(savedDeal));
      if (onSuccess) {
        onSuccess(savedDeal, fields);
      }
    } catch (errors) {
      dispatch(changeDeal({ errors, saving: false }));
    }
  };
}

export function setStandardCalculationMode() {
  return (dispatch, getState) => {
    const { deal } = getState();
    const changes = {
      commissions: deal.commissions.filter(
        commission => commission.transactionSide !== SELL_SIDE || commission.broker?.id
      ),
      hasTransactionSides: false,
      transactionSideDetails: []
    };

    dispatch(changeDeal(changes));
  };
}

export function setTransactionSideCalculationMode() {
  return (dispatch, getState) => {
    const { deal } = getState();
    const changes = {
      hasTransactionSides: true,
      transactionSideDetails: [newTransactionSideDetails(BUY_SIDE, true), newTransactionSideDetails(SELL_SIDE, true)],
    };
    changes.commissions = deal.commissions.map((commission) => {
      const transactionSide = commission.transactionSide || BUY_SIDE;
      return commission.transactionSideDetails?.length
        ? { ...commission, transactionSide }
        : (
          {
            ...commission,
            transactionSide,
            transactionSideDetails: [newTransactionSideDetails(transactionSide, true)]
          }
        );
    });
    
    dispatch(changeDeal(changes));
  };
}

export function changeDealTransactionSide(deal, changes, transactionSide, updatePercent) {
  return async (dispatch) => {
    const updatedDeal = { ...deal };
    updatedDeal.transactionSideDetails = updatedDeal.transactionSideDetails.map(details => (
      details.transactionSide === transactionSide ? { ...details, ...changes } : details
    ));
    updateTransactionSideGrossCommissionValues(updatedDeal, updatePercent);
    updatedDeal.commissions = recalculateBrokerCommissions(updatedDeal);
    dispatch(changeDeal(updatedDeal));
  };
}
